import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Tooltip } from "@/components/common/Tooltip";
import { Disclosure } from "@/components/design/Disclosure";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { SettingLabel } from "./SettingLabel";
import { Switch } from "@/components/design/Switch";
import type { MatchData } from "./match-design-types";
import { matchSubject, ranksCandidates, type UiMatchMode } from "./match-mode";

// Bewusst ohne candidateAssessmentPrompt: das Freitext-Feld ist vorerst aus der UI
// entfernt, weil sich der Zusatz nicht zuverlaessig auf die konfigurierbaren Dimensionen
// begrenzen laesst (Prompt-Injection-Kanal). Gespeicherte Werte round-trippen weiter
// ueber den Codec; genesis ignoriert sie serverseitig.
type PromptData = Pick<
  MatchData,
  | "aiEnabled"
  | "candidateAssessmentDetail"
  | "candidateAssessmentStrictness"
  | "candidateAssessmentOutcomeFocus"
  | "requirementLeniency"
>;

export function CandidateAssessmentPromptEditor({
  data,
  update,
  readOnly,
  mode,
}: {
  data: PromptData;
  update: (patch: Partial<MatchData>) => void;
  readOnly: boolean;
  // DER MODUS STATT ZWEIER SICHTBARKEITS-FLAGS. Welcher Regler in dieser Box ueberhaupt wirkt,
  // haengt allein am Modus, und match-mode.ts ist die eine Stelle, an der ein Modus seine Antworten
  // bekommt. Mit Flags muesste jeder Aufrufer sie richtig setzen, und der naechste Modus waere ein
  // stiller Fehler an der Aufrufstelle statt eine Zeile in der Tabelle.
  mode: UiMatchMode;
}) {
  const intl = useIntl();
  // Ausfuehrlichkeit / Bewertungsmassstab / Ergebnisfokus steuern nur die Kandidaten-Einschaetzung,
  // die genesis ausschliesslich im RANK-Modus erzeugt (MatchResultGenerateController, spark #233).
  // In Assess/Optimize waeren sie wirkungslos, deshalb dort ausblenden.
  const showStyleRegulators = ranksCandidates(mode);
  const forCompanies = matchSubject(mode) === "companies";
  // Ein Schalter fuer die gesamte KI dieses Matches: dataToDesign schreibt ihn als aiMode auf alle
  // KI-faehigen Conditions BEIDER Arme, genesis leitet daraus Einzelpruefungen und (im Rank) die
  // Kandidaten-Einschaetzung ab. Die Stil-Regler sind ohne KI wirkungslos, deshalb dann disabled.
  const controlsDisabled = readOnly || !data.aiEnabled;
  // Zugeklappt, solange die KI aus ist: die Regler darunter wirken dann nicht, und eine
  // dauerhaft offene Box voll wirkungsloser Regler kostet nur Hoehe. Der Schalter klappt
  // mit auf, das Chevron bleibt davon unabhaengig jederzeit klickbar (Zwilling der
  // Agentic-AI- und Team-Fit-Box).
  const [open, setOpen] = useState(data.aiEnabled);

  const aiToggle = (
    <div className="flex items-center gap-2">
      <span className="smycles-field-label leading-none">
        <FormattedMessage id="match.challenge.aiAssist" defaultMessage="KI-Unterstützung" />
      </span>
      <Tooltip
        placement="left"
        content={
          // Die per-Person-Einschaetzung mit Score erzeugt genesis nur im RANK-Modus
          // (MatchResultGenerateController); in Assess/Optimize prueft die KI nur die nicht
          // belegbaren Anforderungen (spark #233). Im Unternehmens-Arm traegt kein Tooltip die
          // Erklaerung: dort stehen die drei Saetze offen unter dem Schalter.
          showStyleRegulators ? (
            <FormattedMessage
              id="match.challenge.aiAssistHint"
              defaultMessage="KI berücksichtigt das ganze Profil und beurteilt, ob Anforderungen auch ohne expliziten Nachweis erfüllt sind. Zusätzlich erstellt sie zu jeder Person eine Einschätzung mit Score."
            />
          ) : (
            <FormattedMessage
              id="match.challenge.aiAssistHintReduced"
              defaultMessage="KI berücksichtigt das ganze Profil und beurteilt, ob Anforderungen auch ohne expliziten Nachweis erfüllt sind."
            />
          )
        }
      >
        <Switch
          checked={data.aiEnabled}
          onChange={(aiEnabled) => {
            update({ aiEnabled });
            setOpen(aiEnabled);
          }}
          disabled={readOnly}
          ariaLabel={intl.formatMessage({
            id: "match.challenge.aiAssist",
            defaultMessage: "KI-Unterstützung",
          })}
        />
      </Tooltip>
    </div>
  );

  const requirementLeniencyControl = (
    <div className="grow basis-[calc(50%-0.5rem)]">
      <SettingLabel
        label={intl.formatMessage({
          id: "match.challenge.requirementLeniency",
          defaultMessage: "Anforderungsprüfung",
        })}
        ariaLabel={intl.formatMessage({
          id: "match.challenge.requirementLeniencyInfo",
          defaultMessage: "Anforderungsprüfung erklären",
        })}
        descriptions={[
          {
            label: intl.formatMessage({
              id: "match.challenge.requirementLeniencyLenient",
              defaultMessage: "Kulant",
            }),
            description: intl.formatMessage(forCompanies ? {
              id: "match.company.requirementLeniencyLenientDescription",
              defaultMessage: "Bei Muss und Kann berücksichtigt die KI zusätzlich den „Über uns“-Text.",
            } : {
              id: "match.challenge.requirementLeniencyLenientDescription",
              defaultMessage:
                "Die geforderte Stufe ist ein Richtwert: vorhandenes Fachwissen genügt.",
            }),
          },
          {
            label: intl.formatMessage({
              id: "match.challenge.requirementLeniencyMustStrict",
              defaultMessage: "Kulant bei Kann",
            }),
            description: intl.formatMessage(forCompanies ? {
              id: "match.company.requirementLeniencyMustStrictDescription",
              defaultMessage: "Bei Muss zählt nur das Fachwissen; bei Kann berücksichtigt die KI zusätzlich den „Über uns“-Text.",
            } : {
              id: "match.challenge.requirementLeniencyMustStrictDescription",
              defaultMessage:
                "Bei Muss-Kriterien muss die Stufe erreicht sein, bei Kann genügt Vorhandensein.",
            }),
          },
          {
            label: intl.formatMessage({
              id: "match.challenge.requirementLeniencyStrict",
              defaultMessage: "Beides streng",
            }),
            description: intl.formatMessage(forCompanies ? {
              id: "match.company.requirementLeniencyStrictDescription",
              defaultMessage: "Bei Muss und Kann zählt nur das Fachwissen. Der „Über uns“-Text wird nicht berücksichtigt.",
            } : {
              id: "match.challenge.requirementLeniencyStrictDescription",
              defaultMessage:
                "Die geforderte Stufe muss bei Muss und Kann erreicht sein.",
            }),
          },
        ]}
      />
      <SegmentedControl
        value={data.requirementLeniency}
        onChange={(requirementLeniency) => update({ requirementLeniency })}
        size="md"
        optionClassName="whitespace-nowrap"
        options={[
          {
            value: "LENIENT",
            disabled: controlsDisabled,
            label: intl.formatMessage({
              id: "match.challenge.requirementLeniencyLenient",
              defaultMessage: "Kulant",
            }),
          },
          {
            value: "MUST_STRICT",
            disabled: controlsDisabled,
            label: intl.formatMessage({
              id: "match.challenge.requirementLeniencyMustStrict",
              defaultMessage: "Kulant bei Kann",
            }),
          },
          {
            value: "STRICT",
            disabled: controlsDisabled,
            label: intl.formatMessage({
              id: "match.challenge.requirementLeniencyStrict",
              defaultMessage: "Beides streng",
            }),
          },
        ]}
        aria-label={intl.formatMessage({
          id: "match.challenge.requirementLeniency",
          defaultMessage: "Anforderungsprüfung",
        })}
      />
    </div>
  );

  if (forCompanies) {
    return (
      <div
        className={`bg-page box-flat px-5 py-3 transition ${
          data.aiEnabled ? "smycles-ai-shadow" : ""
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-smycles-body font-medium">
            <FormattedMessage id="match.challenge.hrPromptLabel" defaultMessage="Bewertungsstil" />
          </span>
          {aiToggle}
        </div>
        <div
          className={`mt-4 transition ${data.aiEnabled ? "" : "opacity-60 blur-[2px]"}`}
          inert={!data.aiEnabled}
        >
          {requirementLeniencyControl}
        </div>
        {/* WAS DER SCHALTER IN EINEM FIRMENLAUF TUT, neben dem Schalter — bis spark #294 stand das
            neben dem Agentic-AI-Schalter, also neben einer anderen Frage. Die ersten beiden Saetze
            sind die REGEL und keine Eigenschaft der KI: sie gelten eingeschaltet wie ausgeschaltet,
            deshalb haengt nur der dritte am Zustand (Konzept v18 (b)). */}
        <div className="mt-2 space-y-1.5 text-smycles-meta leading-relaxed text-ink-muted">
          <p>
            <FormattedMessage
              id="match.company.aiScope"
              defaultMessage="Die KI beurteilt ausschließlich das Fachwissen, sonst nichts. Branche und Mitarbeitendenzahl werden verglichen, nicht beurteilt."
            />
          </p>
          <p>
            <FormattedMessage
              id="match.company.aiUnassessable"
              defaultMessage="Was sie nicht beurteilen kann, zählt als nicht bewertbar und nie als nicht erfüllt."
            />
          </p>
          {!data.aiEnabled ? (
            <p>
              <FormattedMessage
                id="match.company.aiOffMeaning"
                defaultMessage="Ausgeschaltet zählt nur die wörtliche Übereinstimmung; alles andere bleibt nicht bewertbar."
              />
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    // Peer-Karte zu Rollenanforderungen/Team Powers: dieselbe Disclosure (14px-Titel,
    // Chevron, einklappbar) statt des groesseren BoxHeader — die Groesse soll keine
    // Hierarchie behaupten, die es nicht gibt. Das KI-Signal traegt der stateful
    // smycles-ai-shadow ueber className, analog zu den Anforderungs-Karten (UI_CONVENTIONS,
    // Where AI lives); der KI-Schalter sitzt im headerAction-Slot.
    <Disclosure
      open={open}
      onToggle={() => setOpen(!open)}
      className={`transition ${data.aiEnabled ? "smycles-ai-shadow" : ""}`}
      title={
        <FormattedMessage
          id="match.challenge.hrPromptLabel"
          defaultMessage="Bewertungsstil"
        />
      }
      headerAction={aiToggle}
    >
      {/* Flex-Wrap statt Grid: die Optionen der Regler sind einzeilig (nowrap),
          also muss der ganze Block umbrechen duerfen, wenn die halbe Zeile nicht
          reicht. min-width:auto (Flex-Default) verhindert das Ueberlappen, das
          minmax(0,1fr)-Grid zuliess. */}
      {/* Ohne KI finden die Stil-Regler keine Anwendung (genesis liest sie nur im
          AI-Pfad): Blur + Opacity folgt der Blur-Relevanz-Sprache (Team Powers,
          Coming-Soon), inert nimmt Regler UND Info-Tooltips aus Tab-Reihenfolge
          und Interaktion, analog zum zugeklappten Disclosure-Body. */}
      <div
        className={`flex flex-wrap gap-4 transition ${
          data.aiEnabled ? "" : "opacity-60 blur-[2px]"
        }`}
        inert={!data.aiEnabled}
        data-testid="assessment-style-settings"
      >
        {showStyleRegulators && (
        <>
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.assessmentDetail",
              defaultMessage: "Ausführlichkeit",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.assessmentDetailInfo",
              defaultMessage: "Ausführlichkeit erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailBullets",
                  defaultMessage: "Stichpunkte",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentDetailBulletsDescription",
                  defaultMessage: "Kompakte Punkte mit Zeilenumbrüchen.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailSentences",
                  defaultMessage: "Sätze",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentDetailSentencesDescription",
                  defaultMessage: "Kurze, vollständige Sätze.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailLongText",
                  defaultMessage: "Langtext",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentDetailLongTextDescription",
                  defaultMessage: "Ausführliche Begründung mit Belegen.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.candidateAssessmentDetail}
            onChange={(candidateAssessmentDetail) => update({ candidateAssessmentDetail })}
            size="md"
            options={[
              {
                value: "BULLETS",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailBullets",
                  defaultMessage: "Stichpunkte",
                }),
              },
              {
                value: "SENTENCES",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailSentences",
                  defaultMessage: "Sätze",
                }),
              },
              {
                value: "LONG_TEXT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailLongText",
                  defaultMessage: "Langtext",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.assessmentDetail",
              defaultMessage: "Ausführlichkeit",
            })}
          />
        </div>
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.assessmentStrictness",
              defaultMessage: "Bewertungsmaßstab",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.assessmentStrictnessInfo",
              defaultMessage: "Bewertungsmaßstab erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessNormal",
                  defaultMessage: "Normal",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessNormalDescription",
                  defaultMessage: "Verwendet die normale Bewertungskalibrierung.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessStrict",
                  defaultMessage: "Hoch",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessStrictDescription",
                  defaultMessage: "Unklare oder nur angedeutete Erfahrung senkt den Score moderat.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessExtreme",
                  defaultMessage: "Sehr hoch",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessExtremeDescription",
                  defaultMessage: "Nur explizite, rollenrelevante Belege zählen; jede Lücke kostet deutlich Punkte.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.candidateAssessmentStrictness}
            onChange={(candidateAssessmentStrictness) =>
              update({ candidateAssessmentStrictness })}
            size="md"
            options={[
              {
                value: "NORMAL",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessNormal",
                  defaultMessage: "Normal",
                }),
              },
              {
                value: "STRICT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessStrict",
                  defaultMessage: "Hoch",
                }),
              },
              {
                value: "EXTREME",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessExtreme",
                  defaultMessage: "Sehr hoch",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.assessmentStrictness",
              defaultMessage: "Bewertungsmaßstab",
            })}
          />
        </div>
        </>
        )}
        {requirementLeniencyControl}
        {showStyleRegulators && (
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.assessmentOutcomeFocus",
              defaultMessage: "Ergebnisfokus",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.assessmentOutcomeFocusInfo",
              defaultMessage: "Ergebnisfokus erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeRisks",
                  defaultMessage: "Lücken",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeRisksDescription",
                  defaultMessage: "Prüft den Lebenslauf gezielt auf Lücken und benennt sie zuerst.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeBalanced",
                  defaultMessage: "Ausgewogen",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeBalancedDescription",
                  defaultMessage: "Bewertet neutral, Stärken und Lücken gleichgewichtet.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeOpportunities",
                  defaultMessage: "Stärken",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeOpportunitiesDescription",
                  defaultMessage: "Bewertet wohlwollend: Stärken und Einsatzmöglichkeiten zuerst.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.candidateAssessmentOutcomeFocus}
            onChange={(candidateAssessmentOutcomeFocus) =>
              update({ candidateAssessmentOutcomeFocus })}
            size="md"
            options={[
              {
                value: "RISKS",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeRisks",
                  defaultMessage: "Lücken",
                }),
              },
              {
                value: "BALANCED",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeBalanced",
                  defaultMessage: "Ausgewogen",
                }),
              },
              {
                value: "OPPORTUNITIES",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeOpportunities",
                  defaultMessage: "Stärken",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.assessmentOutcomeFocus",
              defaultMessage: "Ergebnisfokus",
            })}
          />
        </div>
        )}
      </div>
    </Disclosure>
  );
}
